/**
 * Kinetic Theme
 *
 * Brutalist design with dark mode and motion-heavy aesthetics.
 * Inspired by brutalist web design: bold contrasts, raw typography, stark geometry.
 */

import { Theme } from '../types';
import { TextStyle, ViewStyle } from 'react-native';

export const kinetic: Theme = {
  id: 'kinetic',
  name: 'Kinetic',

  colors: {
    // Primary - Acid Yellow
    primary: '#DFE104',
    primaryLight: '#FFFF40',
    primaryDark: '#B8B803',

    // Secondary - Neon Cyan
    secondary: '#00F5FF',
    accent: '#FF00FF', // Neon Magenta

    // Status colors
    success: '#00FF00', // Neon green
    warning: '#FFFF00', // Yellow
    warningBackground: '#2A2A00', // Dark yellow background
    warningBorder: '#FFFF00', // Yellow border
    warningText: '#FFFF00', // Yellow text
    error: '#FF0055', // Hot pink-red
    info: '#00F5FF', // Cyan

    // Neutral grays - dark theme
    background: '#09090B', // Rich black
    backgroundSecondary: '#141414',
    backgroundElevated: '#1E1E1E',

    surface: '#1E1E1E',
    surfaceSecondary: '#2A2A2A',

    text: '#FAFAFA', // Off-white
    textSecondary: '#A1A1AA', // Zinc 400
    textTertiary: '#71717A', // Zinc 500
    textInverse: '#000000',

    border: '#3F3F46', // Zinc 700
    borderLight: '#27272A',
    divider: '#27272A',

    // Overlays
    overlay: 'rgba(0, 0, 0, 0.8)',
    overlayLight: 'rgba(0, 0, 0, 0.5)',

    // Selected state background
    selectedBackground: '#2A2A2E', // Dark gray for selected options

    // Chart colors - Kinetic neon palette: vibrant colors for dark backgrounds
    chartColors: [
      '#DFE104', // Acid Yellow (primary)
      '#00F5FF', // Neon Cyan
      '#FF00FF', // Neon Magenta
      '#00FF88', // Neon Green
      '#FF6B35', // Neon Orange
      '#7B68EE', // Medium Slate Blue
      '#FF1493', // Deep Pink
      '#00CED1', // Dark Turquoise
      '#FFD700', // Gold
      '#9370DB', // Medium Purple
    ],
  },

  typography: {
    // Font sizes (4px base scale, slightly larger for impact)
    fontSizeXs: 12,
    fontSizeSm: 14,
    fontSizeMd: 16,
    fontSizeLg: 18,
    fontSizeXl: 22,
    fontSize2Xl: 28,
    fontSize3Xl: 36,
    fontSize4Xl: 48,

    // Font weights (bold for brutalist aesthetic)
    fontWeightLight: 300,
    fontWeightNormal: 400,
    fontWeightMedium: 600,
    fontWeightSemibold: 700,
    fontWeightBold: 900,

    // Line heights (tighter for kinetic feel)
    lineHeightTight: 1.1,
    lineHeightNormal: 1.4,
    lineHeightRelaxed: 1.6,

    // Letter spacing (wider for impact)
    letterSpacingTight: -0.5,
    letterSpacingNormal: 0,
    letterSpacingWide: 1,
  },

  spacing: {
    unit: 4,
    xxs: 2,
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    '2xl': 24,
    '3xl': 32,
    '4xl': 40,
    '5xl': 48,
  },

  radii: {
    none: 0, // Brutalist sharp edges
    sm: 2,
    md: 4,
    lg: 8,
    xl: 12,
    full: 9999,
  },

  shadows: {
    none: {} as ViewStyle,
    sm: {
      shadowColor: '#DFE104',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.3,
      shadowRadius: 4,
      elevation: 2,
    } as ViewStyle,
    md: {
      shadowColor: '#DFE104',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.5,
      shadowRadius: 8,
      elevation: 4,
    } as ViewStyle,
    lg: {
      shadowColor: '#FF00FF',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.6,
      shadowRadius: 16,
      elevation: 6,
    } as ViewStyle,
    xl: {
      shadowColor: '#FF00FF',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.8,
      shadowRadius: 24,
      elevation: 8,
    } as ViewStyle,
  },

  breakpoints: {
    sm: 375,
    md: 768,
    lg: 1024,
    xl: 1280,
  },

  motion: {
    durationFast: 100,
    durationNormal: 200,
    durationSlow: 400,

    easingDefault: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easingIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easingOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easingInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  presets: {
    heading1: {
      fontSize: 48,
      fontWeight: '900',
      color: '#FFFFFF',
      letterSpacing: 1,
      textTransform: 'uppercase',
    } as TextStyle,

    heading2: {
      fontSize: 36,
      fontWeight: '900',
      color: '#FFFFFF',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    } as TextStyle,

    heading3: {
      fontSize: 28,
      fontWeight: '700',
      color: '#DFE104',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    } as TextStyle,

    body: {
      fontSize: 16,
      fontWeight: '400',
      color: '#FAFAFA',
      lineHeight: 22,
    } as TextStyle,

    caption: {
      fontSize: 12,
      fontWeight: '500',
      color: '#A1A1AA',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    } as TextStyle,

    card: {
      backgroundColor: '#1E1E1E',
      borderWidth: 2,
      borderColor: '#3F3F46',
      borderRadius: 0,
      padding: 16,
      shadowColor: '#DFE104',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 4,
    } as ViewStyle,

    buttonPrimary: {
      backgroundColor: '#DFE104',
      borderWidth: 2,
      borderColor: '#3F3F46',
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: 0,
      minHeight: 48,
    } as ViewStyle,

    buttonSecondary: {
      backgroundColor: '#1E1E1E',
      borderWidth: 2,
      borderColor: '#FAFAFA',
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: 0,
      minHeight: 48,
    } as ViewStyle,

    buttonDanger: {
      backgroundColor: '#FF0055',
      borderWidth: 2,
      borderColor: '#3F3F46',
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: 0,
      minHeight: 48,
    } as ViewStyle,

    input: {
      backgroundColor: '#141414',
      borderWidth: 2,
      borderColor: '#3F3F46',
      borderRadius: 0,
      paddingVertical: 14,
      paddingHorizontal: 16,
      fontSize: 16,
      color: '#FAFAFA',
    } as ViewStyle,
  },
};
